import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tabs,
  Tab,
  Grid,
  Switch,
  FormControlLabel,
  SelectChangeEvent,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  ContentCopy as ContentCopyIcon,
  Info as InfoIcon,
  Analytics as AnalyticsIcon,
  Payment as PaymentIcon,
  Link as LinkIcon,
  Save as SaveIcon,
  MarkEmailRead as MarkEmailReadIcon,
  History as HistoryIcon,
  ErrorOutline as ErrorOutlineIcon,
} from "@mui/icons-material";
import { Player, Division, LeagueDay } from "../types";
import { apiRequest } from "../config/api";
import { useDivision } from "../contexts/DivisionContext";
import { useSeason } from "../contexts/SeasonContext";
import DivisionSelector from "../components/DivisionSelector";
import SeasonSelector from "../components/SeasonSelector";

interface PlayerFinance {
  playerId: string;
  playerName: string;
  registeredDivision: Division; // Player's registered division
  totalLeagueDays: number;
  leagueDaysInOwnDivision: number;
  leagueDaysInOtherDivisions: number;
  trialDaysCount: number; // Number of trial days attended
  leagueDayAdjustment: number; // Cumulative adjustment from penalties/bonuses
  isGuest: boolean; // Guest players pay €22 per day from day 1
  paidLeagueDays: number[]; // Array of league day numbers that have been paid
  unpaidLeagueDays: number[]; // Array of league day numbers that need payment
  totalOwed: number;
}

interface UnpaidLeagueDayDetail {
  dayNumber: number;
  date: string;
  division: string;
}

interface PaymentReminderLogEntry {
  id: string;
  playerId: string;
  playerName: string;
  email: string;
  sentAt: string;
  success: boolean;
  errorMessage?: string;
  subject: string;
  htmlBody: string;
  totalOwed: number;
  unpaidLeagueDays: UnpaidLeagueDayDetail[];
  triggeredBy: string;
}

interface LeagueDayParticipation {
  leagueDayId: string;
  date: string;
  division: Division;
  dayNumber: number;
  isPaid: boolean;
  isPayable: boolean; // Whether this day requires payment
}

interface AnalyticsSummary {
  totalPaid: number;
  totalOutstanding: number;
  totalRevenue: number;
  playerCount: number;
  averagePaidPerPlayer: number;
  guestPlayerCount: number;
  regularPlayerCount: number;
  guestTotalPaid: number;
  regularTotalPaid: number;
  guestTotalOutstanding: number;
  regularTotalOutstanding: number;
}

interface DivisionBreakdown {
  division: Division;
  totalPaid: number;
  totalOutstanding: number;
  playerCount: number;
  guestPlayerCount: number;
  regularPlayerCount: number;
}

interface PlayerBreakdown {
  playerId: string;
  playerName: string;
  division: Division;
  isGuest: boolean;
  totalLeagueDays: number;
  paidLeagueDays: number;
  unpaidLeagueDays: number;
  amountPaid: number;
  amountOutstanding: number;
}

interface AnalyticsData {
  summary: AnalyticsSummary;
  divisionBreakdown: DivisionBreakdown[];
  playerBreakdown: PlayerBreakdown[];
  topContributors: PlayerBreakdown[];
}

const PRICE_PER_LEAGUE_DAY = 18; // Euros for regular players
const GUEST_PRICE_PER_LEAGUE_DAY = 20; // Euros for guest players
const FREE_LEAGUE_DAYS = 5; // Only for regular players, guests pay from day 1


function Finances() {
  const { currentDivision } = useDivision();
  const { selectedSeason } = useSeason();

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // PayPal link config
  const [paypalLink, setPaypalLink] = useState("");
  const [paypalLinkDraft, setPaypalLinkDraft] = useState("");
  const [paypalLinkSaved, setPaypalLinkSaved] = useState(false);

  // Existing states
  const [players, setPlayers] = useState<Player[]>([]);
  const [leagueDays, setLeagueDays] = useState<LeagueDay[]>([]);
  const [penalties, setPenalties] = useState<any[]>([]);
  const [playerFinances, setPlayerFinances] = useState<PlayerFinance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerFinance | null>(
    null,
  );
  const [playerLeagueDays, setPlayerLeagueDays] = useState<
    LeagueDayParticipation[]
  >([]);

  // Analytics states
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(
    null,
  );
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDivision, setAnalyticsDivision] = useState<Division | "ALL">(
    "ALL",
  );
  const [analyticsPlayerId, setAnalyticsPlayerId] = useState<string>("");
  const [guestOnlyFilter, setGuestOnlyFilter] = useState<boolean | null>(null);

  // Payment reminder email states
  const [sendingEmailId, setSendingEmailId] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);
  const [reminderLog, setReminderLog] = useState<PaymentReminderLogEntry[]>(
    [],
  );
  // Payment reminder compose/preview dialog — lets an admin review and edit
  // the email before it's actually sent.
  const [reminderDialogFinance, setReminderDialogFinance] =
    useState<PlayerFinance | null>(null);
  const [reminderDialogError, setReminderDialogError] = useState<
    string | null
  >(null);
  const [reminderSubjectDraft, setReminderSubjectDraft] = useState("");
  const [reminderHtmlBodyDraft, setReminderHtmlBodyDraft] = useState("");
  const [reminderSending, setReminderSending] = useState(false);
  const [reminderLogLoading, setReminderLogLoading] = useState(false);
  const [selectedLogEntry, setSelectedLogEntry] =
    useState<PaymentReminderLogEntry | null>(null);

  useEffect(() => {
    fetchData();
  }, [currentDivision, selectedSeason]);

  useEffect(() => {
    apiRequest("/api/finances/payment-config")
      .then((r) => r.json())
      .then((data) => {
        setPaypalLink(data.paypalLink || "");
        setPaypalLinkDraft(data.paypalLink || "");
      })
      .catch(() => {});
  }, []);

  // Fetch the reminder log on mount (not just when the log tab is active) so the
  // "last sent" hint is available under the send button right away.
  useEffect(() => {
    fetchReminderLog();
  }, []);

  const lastReminderByPlayer = useMemo(() => {
    const map = new Map<string, PaymentReminderLogEntry>();
    // reminderLog is ordered by sentAt desc, so the first match per player is the latest.
    for (const entry of reminderLog) {
      if (entry.success && !map.has(entry.playerId)) {
        map.set(entry.playerId, entry);
      }
    }
    return map;
  }, [reminderLog]);

  const formatLastReminderText = (playerId: string): string => {
    const entry = lastReminderByPlayer.get(playerId);
    if (!entry) return "No payment reminder email sent yet";
    const when = new Date(entry.sentAt).toLocaleString();
    const via = entry.triggeredBy === "manual" ? "manual" : "automatic";
    return `Last sent ${when} (${via})`;
  };

  useEffect(() => {
    if (activeTab === 1) {
      fetchAnalytics();
    }
    if (activeTab === 2) {
      fetchReminderLog();
    }
  }, [
    activeTab,
    analyticsDivision,
    analyticsPlayerId,
    guestOnlyFilter,
    selectedSeason,
  ]);

  const fetchReminderLog = async () => {
    setReminderLogLoading(true);
    try {
      const response = await apiRequest("/api/finances/payment-reminders/log");
      if (response.ok) {
        const data = await response.json();
        setReminderLog(data.log || []);
      }
    } catch (error) {
      console.error("Error fetching payment reminder log:", error);
    } finally {
      setReminderLogLoading(false);
    }
  };

  const fetchData = async () => {
    if (!selectedSeason) return;

    setLoading(true);
    try {
      // Fetch players, league days, and penalties in parallel
      const leagueDaysParams = new URLSearchParams({
        seasonId: selectedSeason.id,
      });
      const penaltiesParams = new URLSearchParams({
        seasonId: selectedSeason.id,
      });

      const [playersResponse, leagueDaysResponse, penaltiesResponse] =
        await Promise.all([
          apiRequest(`/api/players?division=${currentDivision}`),
          apiRequest(`/api/league-days?${leagueDaysParams.toString()}`),
          apiRequest(`/api/penalties?${penaltiesParams.toString()}`),
        ]);

      let leagueDaysData: LeagueDay[] = [];
      if (leagueDaysResponse.ok) {
        leagueDaysData = await leagueDaysResponse.json();
        setLeagueDays(leagueDaysData);
      }

      if (penaltiesResponse.ok) {
        const penaltiesData = await penaltiesResponse.json();
        setPenalties(penaltiesData);
      }

      if (playersResponse.ok) {
        const playersData = await playersResponse.json();

        // Collect all player IDs that participated in this season's league days
        // (covers guest players added by admin who may not have currentSeason set)
        const seasonLeagueDayPlayerIds = new Set<string>(
          leagueDaysData.flatMap((ld: any) => [
            ...(ld.selectedPlayers || []),
            ...(ld.substitutions || []).map((s: any) => s.substitutePlayerId),
          ]),
        );

        // Include players approved for this season OR who participated in a league day this season
        const seasonPlayers = playersData.filter(
          (p: any) =>
            p.currentSeason === selectedSeason.id ||
            seasonLeagueDayPlayerIds.has(p.id),
        );
        setPlayers(seasonPlayers);
      }

      setLoading(false);
    } catch (error) {
      console.error("Errors fetching finance data:", error);
      setError("Failed to load finance data");
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    if (!selectedSeason) return;

    setAnalyticsLoading(true);
    try {
      const params = new URLSearchParams({
        seasonId: selectedSeason.id,
      });

      if (analyticsDivision !== "ALL") {
        params.append("division", analyticsDivision);
      }

      if (analyticsPlayerId) {
        params.append("playerId", analyticsPlayerId);
      }

      if (guestOnlyFilter !== null) {
        params.append("guestOnly", guestOnlyFilter.toString());
      }

      const response = await apiRequest(
        `/api/finances/analytics?${params.toString()}`,
      );

      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data);
      } else {
        setError("Failed to fetch analytics data");
      }

      setAnalyticsLoading(false);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      setError("Failed to load analytics data");
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (players.length > 0 && leagueDays.length > 0) {
      calculatePlayerFinances();
    }
  }, [players, leagueDays, penalties, selectedSeason]);

  const calculatePlayerFinances = () => {
    const finances: PlayerFinance[] = players.map((player) => {
      // Count actual participation in completed league days
      const completedLeagueDays = leagueDays.filter((ld) => ld.isCompleted);

      let leagueDaysInOwnDivision = 0;
      let leagueDaysInOtherDivisions = 0;

      completedLeagueDays.forEach((ld) => {
        // Check if player participated (either in selectedPlayers or as substitute)
        const wasSelected = ld.selectedPlayers?.includes(player.id);
        const wasSubstitute = ld.substitutions?.some(
          (sub: any) => sub.substitutePlayerId === player.id,
        );

        if (wasSelected || wasSubstitute) {
          if (ld.division === player.division) {
            leagueDaysInOwnDivision++;
          } else {
            leagueDaysInOtherDivisions++;
          }
        }
      });

      // Calculate cumulative league day adjustment from penalties/bonuses
      const playerPenaltiesAndBonuses = penalties.filter(
        (p: any) => p.playerId === player.id && !p.isDisqualification,
      );
      const leagueDayAdjustment = playerPenaltiesAndBonuses.reduce(
        (sum: number, p: any) => {
          const adjustment = p.leagueDayAdjustment || 0;
          // Penalties are positive (+), bonuses are negative (-)
          return sum + (p.type === "penalty" ? adjustment : -adjustment);
        },
        0,
      );

      // Add trial days to the total
      const trialDaysCount = player.trialDaysCount || 0;
      // Treat as guest if flagged, or if not approved for the selected season
      const isGuest =
        player.isGuest ||
        (selectedSeason ? player.currentSeason !== selectedSeason.id : false);

      // Calculate total with adjustment
      const actualDays =
        leagueDaysInOwnDivision + leagueDaysInOtherDivisions + trialDaysCount;
      const totalLeagueDays = actualDays + leagueDayAdjustment;

      // Get paid league days from player data, scoped to the selected season
      const paidLeagueDays: number[] =
        (selectedSeason && player.paidLeagueDaysBySeason?.[selectedSeason.id]) || [];

      // Calculate which league days need payment
      const unpaidLeagueDays: number[] = [];

      if (isGuest) {
        // Guest players pay from day 1 at €22 per day
        for (let i = 1; i <= totalLeagueDays; i++) {
          if (!paidLeagueDays.includes(i)) {
            unpaidLeagueDays.push(i);
          }
        }
      } else {
        // Regular players get 5 free days, then pay €20 per day
        for (let i = FREE_LEAGUE_DAYS + 1; i <= totalLeagueDays; i++) {
          if (!paidLeagueDays.includes(i)) {
            unpaidLeagueDays.push(i);
          }
        }
      }

      const totalOwed =
        unpaidLeagueDays.length *
        (isGuest ? GUEST_PRICE_PER_LEAGUE_DAY : PRICE_PER_LEAGUE_DAY);

      return {
        playerId: player.id,
        playerName: player.name,
        registeredDivision: player.division,
        totalLeagueDays,
        leagueDaysInOwnDivision,
        leagueDaysInOtherDivisions,
        trialDaysCount,
        leagueDayAdjustment,
        isGuest,
        paidLeagueDays,
        unpaidLeagueDays,
        totalOwed,
      };
    });

    // Sort by total owed (descending) and then by name
    finances.sort((a, b) => {
      if (b.totalOwed !== a.totalOwed) {
        return b.totalOwed - a.totalOwed;
      }
      return a.playerName.localeCompare(b.playerName);
    });

    setPlayerFinances(finances);
  };

  const handleTogglePayment = async (
    playerId: string,
    leagueDayNumber: number,
  ) => {
    if (!selectedSeason) return;
    try {
      const response = await apiRequest(
        `/api/finances/player/${playerId}/toggle-payment`,
        {
          method: "POST",
          body: JSON.stringify({ leagueDayNumber, seasonId: selectedSeason.id }),
        },
      );

      if (response.ok) {
        // Refresh data to update UI
        fetchData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to update payment status");
      }
    } catch (error) {
      console.error("Error toggling payment:", error);
      setError("Failed to update payment status");
    }
  };

  const getPaymentStatus = (finance: PlayerFinance) => {
    if (finance.isGuest) {
      // Guest players always owe from day 1
      if (finance.unpaidLeagueDays.length > 0) {
        return {
          type: "unpaid",
          message: `${finance.unpaidLeagueDays.length} unpaid league days (Guest)`,
        };
      }
      return { type: "paid", message: "All paid (Guest)" };
    }

    // Regular players
    if (finance.totalLeagueDays <= FREE_LEAGUE_DAYS) {
      return { type: "free", message: "All league days free" };
    }
    if (finance.totalLeagueDays === FREE_LEAGUE_DAYS) {
      return { type: "warning", message: "Next league day requires payment" };
    }
    if (finance.unpaidLeagueDays.length > 0) {
      return {
        type: "unpaid",
        message: `${finance.unpaidLeagueDays.length} unpaid league days`,
      };
    }
    return { type: "paid", message: "All paid" };
  };

  const handlePaypalLinkSave = async () => {
    if (paypalLinkDraft === paypalLink) return;
    try {
      await apiRequest("/api/finances/payment-config", {
        method: "POST",
        body: JSON.stringify({ paypalLink: paypalLinkDraft }),
      });
      setPaypalLink(paypalLinkDraft);
      setPaypalLinkSaved(true);
      setTimeout(() => setPaypalLinkSaved(false), 2000);
    } catch {
      setError("Failed to save PayPal link");
    }
  };

  const handleCopyWhatsAppMessage = async (finance: PlayerFinance) => {
    const unpaidDaysText = finance.unpaidLeagueDays.join(", ");

    // Build payment link — append amount to base URL if one is configured
    const paymentLink = paypalLink
      ? `${paypalLink.replace(/\/$/, "")}/${finance.totalOwed}EUR`
      : null;

    const message = `Hey ${finance.playerName},
Hope you are doing well.
Please dont forget to transfer the money for the League day(s) you played number [${unpaidDaysText}], the total amount is €${finance.totalOwed}${
      paymentLink
        ? `

You can pay via PayPal here:
${paymentLink}`
        : ""
    }`;

    try {
      await navigator.clipboard.writeText(message);
      setCopySuccess(finance.playerId);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) {
      console.error("Failed to copy message:", err);
      setError("Failed to copy message to clipboard");
    }
  };

  const handleOpenReminderDialog = async (finance: PlayerFinance) => {
    setSendingEmailId(finance.playerId);
    try {
      const response = await apiRequest(
        `/api/finances/payment-reminders/preview/${finance.playerId}`,
      );
      const data = await response.json();

      if (response.ok && data.success) {
        setReminderSubjectDraft(data.subject);
        setReminderHtmlBodyDraft(data.htmlBody);
        setReminderDialogError(null);
        setReminderDialogFinance(finance);
      } else {
        setError(data.error || "Failed to preview payment reminder email");
      }
    } catch (err) {
      console.error("Failed to preview payment reminder email:", err);
      setError("Failed to preview payment reminder email");
    } finally {
      setSendingEmailId(null);
    }
  };

  const handleCloseReminderDialog = () => {
    if (reminderSending) return;
    setReminderDialogFinance(null);
    setReminderDialogError(null);
  };

  const handleConfirmSendReminderEmail = async () => {
    if (!reminderDialogFinance) return;
    setReminderSending(true);
    setReminderDialogError(null);
    try {
      const response = await apiRequest(
        `/api/finances/payment-reminders/send/${reminderDialogFinance.playerId}`,
        {
          method: "POST",
          body: JSON.stringify({
            subject: reminderSubjectDraft,
            htmlBody: reminderHtmlBodyDraft,
          }),
        },
      );
      const data = await response.json();

      if (response.ok && data.success) {
        setEmailSuccess(
          `Payment reminder email sent to ${reminderDialogFinance.playerName}`,
        );
        setTimeout(() => setEmailSuccess(null), 3000);
        fetchReminderLog();
        setReminderDialogFinance(null);
      } else {
        setReminderDialogError(
          data.error || "Failed to send payment reminder email",
        );
      }
    } catch (err) {
      console.error("Failed to send payment reminder email:", err);
      setReminderDialogError("Failed to send payment reminder email");
    } finally {
      setReminderSending(false);
    }
  };

  const handlePlayerClick = (finance: PlayerFinance) => {
    // Build list of league days this player participated in
    const participatedDays: LeagueDayParticipation[] = [];
    let dayCounter = 0;

    // Get all completed league days sorted by date
    const completedLeagueDays = leagueDays
      .filter((ld) => ld.isCompleted)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    completedLeagueDays.forEach((ld) => {
      // Check if player participated (either in selectedPlayers or as substitute)
      const wasSelected = ld.selectedPlayers?.includes(finance.playerId);
      const wasSubstitute = ld.substitutions?.some(
        (sub: any) => sub.substitutePlayerId === finance.playerId,
      );

      if (wasSelected || wasSubstitute) {
        dayCounter++;
        const isPayable = finance.isGuest
          ? true
          : dayCounter > FREE_LEAGUE_DAYS;
        const isPaid = finance.paidLeagueDays.includes(dayCounter);

        participatedDays.push({
          leagueDayId: ld.id,
          date: ld.date.toString(),
          division: ld.division,
          dayNumber: dayCounter,
          isPaid,
          isPayable,
        });
      }
    });

    setPlayerLeagueDays(participatedDays);
    setSelectedPlayer(finance);
  };

  const handleCloseDialog = () => {
    setSelectedPlayer(null);
    setPlayerLeagueDays([]);
  };

  if (loading) {
    return (
      <Box p={3}>
        <Typography>Loading finance data...</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        mb={3}
        gap={{ xs: 2, sm: 0 }}
      >
        <Typography variant="h4">Finances</Typography>
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          gap={1}
          width={{ xs: "100%", sm: "auto" }}
        >
          <SeasonSelector fullWidth={false} excludeDrafts={true} />
          <DivisionSelector />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {copySuccess && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setCopySuccess(null)}
        >
          WhatsApp message copied to clipboard!
        </Alert>
      )}

      {emailSuccess && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setEmailSuccess(null)}
        >
          {emailSuccess}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: "divider",
        }}
        variant="fullWidth"
      >
        <Tab
          icon={<PaymentIcon sx={{ display: { xs: "none", sm: "block" } }} />}
          label="Payments"
          iconPosition="start"
        />
        <Tab
          icon={<AnalyticsIcon sx={{ display: { xs: "none", sm: "block" } }} />}
          label="Analytics"
          iconPosition="start"
        />
        <Tab
          icon={<HistoryIcon sx={{ display: { xs: "none", sm: "block" } }} />}
          label="Reminder Log"
          iconPosition="start"
        />
      </Tabs>

      {activeTab === 0 && (
        <Card>
          <CardContent>
            {/* PayPal link config — used in WhatsApp payment reminders */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
              <TextField
                label="PayPal payment link"
                placeholder="https://paypal.me/yourname"
                value={paypalLinkDraft}
                onChange={(e) => setPaypalLinkDraft(e.target.value)}
                onBlur={handlePaypalLinkSave}
                size="small"
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                helperText="Saved automatically. Amount is appended when sending WhatsApp reminders."
              />
              {paypalLinkSaved && (
                <Chip
                  icon={<SaveIcon />}
                  label="Saved"
                  color="success"
                  size="small"
                  sx={{ mt: -2.5 }}
                />
              )}
            </Box>

            <Typography variant="h6" gutterBottom>
              {currentDivision} - Payment Overview
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Regular players: First {FREE_LEAGUE_DAYS} league days are free.
              Starting from league day {FREE_LEAGUE_DAYS + 1}, each costs €
              {PRICE_PER_LEAGUE_DAY}.
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              sx={{ mb: 2 }}
            >
              Guest players: Pay €{GUEST_PRICE_PER_LEAGUE_DAY} per league day
              from day 1 (no free days).
            </Typography>

            {/* Desktop Table View */}
            <TableContainer
              component={Paper}
              sx={{ mt: 2, display: { xs: "none", md: "block" } }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Player</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Total Days</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Own Division</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Other Divisions</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Trial Days</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Bonus/Penalty</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Status</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Payable Days</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Total Owed</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {playerFinances.map((finance) => {
                    const status = getPaymentStatus(finance);
                    return (
                      <TableRow
                        key={finance.playerId}
                        sx={{
                          backgroundColor: finance.isGuest
                            ? "rgba(255, 152, 0, 0.1)"
                            : "inherit",
                          borderLeft: finance.isGuest
                            ? "4px solid #ff9800"
                            : "none",
                        }}
                      >
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() => handlePlayerClick(finance)}
                          >
                            {finance.playerName}
                            {finance.isGuest && (
                              <Chip
                                label="GUEST"
                                size="small"
                                sx={{
                                  backgroundColor: "#ff9800",
                                  color: "white",
                                  fontWeight: "bold",
                                  fontSize: "0.65rem",
                                }}
                              />
                            )}
                            <IconButton size="small" color="primary">
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={finance.totalLeagueDays}
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {finance.leagueDaysInOwnDivision}
                        </TableCell>
                        <TableCell align="center">
                          {finance.leagueDaysInOtherDivisions}
                        </TableCell>
                        <TableCell align="center">
                          {finance.trialDaysCount}
                        </TableCell>
                        <TableCell align="center">
                          {finance.leagueDayAdjustment !== 0 ? (
                            <Chip
                              label={
                                finance.leagueDayAdjustment > 0
                                  ? `+${finance.leagueDayAdjustment}`
                                  : finance.leagueDayAdjustment
                              }
                              size="small"
                              color={
                                finance.leagueDayAdjustment > 0
                                  ? "warning"
                                  : "success"
                              }
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {status.type === "free" && (
                            <Chip
                              label={status.message}
                              color="success"
                              size="small"
                            />
                          )}
                          {status.type === "warning" && (
                            <Chip
                              label={status.message}
                              color="warning"
                              size="small"
                              icon={<WarningIcon />}
                            />
                          )}
                          {status.type === "unpaid" && (
                            <Chip
                              label={status.message}
                              color="error"
                              size="small"
                            />
                          )}
                          {status.type === "paid" && (
                            <Chip
                              label={status.message}
                              color="success"
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {(finance.isGuest ||
                            finance.totalLeagueDays > FREE_LEAGUE_DAYS) && (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                flexWrap: "wrap",
                                justifyContent: "center",
                              }}
                            >
                              {Array.from(
                                {
                                  length: finance.isGuest
                                    ? finance.totalLeagueDays
                                    : finance.totalLeagueDays -
                                      FREE_LEAGUE_DAYS,
                                },
                                (_, i) =>
                                  finance.isGuest
                                    ? i + 1
                                    : i + FREE_LEAGUE_DAYS + 1,
                              ).map((dayNumber) => {
                                const isPaid =
                                  finance.paidLeagueDays.includes(dayNumber);
                                return (
                                  <Chip
                                    key={dayNumber}
                                    label={dayNumber}
                                    size="small"
                                    color={isPaid ? "success" : "error"}
                                    onClick={() =>
                                      handleTogglePayment(
                                        finance.playerId,
                                        dayNumber,
                                      )
                                    }
                                    onDelete={() =>
                                      handleTogglePayment(
                                        finance.playerId,
                                        dayNumber,
                                      )
                                    }
                                    deleteIcon={
                                      isPaid ? (
                                        <CheckCircleIcon />
                                      ) : (
                                        <CancelIcon />
                                      )
                                    }
                                    sx={{ cursor: "pointer" }}
                                  />
                                );
                              })}
                            </Box>
                          )}
                          {!finance.isGuest &&
                            finance.totalLeagueDays <= FREE_LEAGUE_DAYS && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            )}
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              gap: 1,
                            }}
                          >
                            <Box>
                              <Typography
                                variant="body1"
                                fontWeight="bold"
                                color={
                                  finance.totalOwed > 0
                                    ? "error.main"
                                    : "text.secondary"
                                }
                              >
                                €{finance.totalOwed}
                              </Typography>
                              {finance.isGuest && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  (€{GUEST_PRICE_PER_LEAGUE_DAY}/day)
                                </Typography>
                              )}
                            </Box>
                            {finance.totalOwed > 0 && (
                              <IconButton
                                size="small"
                                color={
                                  copySuccess === finance.playerId
                                    ? "success"
                                    : "primary"
                                }
                                onClick={() =>
                                  handleCopyWhatsAppMessage(finance)
                                }
                                title="Copy WhatsApp message"
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            )}
                            {finance.totalOwed > 0 && (
                              <Tooltip
                                title={formatLastReminderText(
                                  finance.playerId,
                                )}
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={
                                      sendingEmailId === finance.playerId
                                    }
                                    onClick={() =>
                                      handleOpenReminderDialog(finance)
                                    }
                                  >
                                    {sendingEmailId === finance.playerId ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <MarkEmailReadIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Mobile Card View */}
            <Box sx={{ mt: 2, display: { xs: "block", md: "none" } }}>
              {playerFinances.map((finance) => {
                const status = getPaymentStatus(finance);
                return (
                  <Card
                    key={finance.playerId}
                    variant="outlined"
                    sx={{
                      mb: 3,
                      borderLeft: finance.isGuest
                        ? "4px solid #ff9800"
                        : "none",
                      backgroundColor: finance.isGuest
                        ? "rgba(255, 152, 0, 0.05)"
                        : "inherit",
                      boxShadow: 2,
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            cursor: "pointer",
                          }}
                          onClick={() => handlePlayerClick(finance)}
                        >
                          <Typography variant="h6" fontWeight="bold">
                            {finance.playerName}
                          </Typography>
                          <IconButton size="small" color="primary">
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        {finance.isGuest && (
                          <Chip
                            label="GUEST"
                            size="small"
                            sx={{
                              backgroundColor: "#ff9800",
                              color: "white",
                              fontWeight: "bold",
                            }}
                          />
                        )}
                      </Box>

                      <Divider sx={{ mb: 2 }} />

                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Total Days
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {finance.totalLeagueDays}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Status
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            {status.type === "free" && (
                              <Chip
                                label={status.message}
                                color="success"
                                size="small"
                              />
                            )}
                            {status.type === "warning" && (
                              <Chip
                                label={status.message}
                                color="warning"
                                size="small"
                                icon={<WarningIcon />}
                              />
                            )}
                            {status.type === "unpaid" && (
                              <Chip
                                label={status.message}
                                color="error"
                                size="small"
                              />
                            )}
                            {status.type === "paid" && (
                              <Chip
                                label={status.message}
                                color="success"
                                size="small"
                              />
                            )}
                          </Box>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">
                            Own Div
                          </Typography>
                          <Typography variant="body2">
                            {finance.leagueDaysInOwnDivision}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">
                            Other Div
                          </Typography>
                          <Typography variant="body2">
                            {finance.leagueDaysInOtherDivisions}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">
                            Trial Days
                          </Typography>
                          <Typography variant="body2">
                            {finance.trialDaysCount}
                          </Typography>
                        </Grid>
                      </Grid>

                      {finance.leagueDayAdjustment !== 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Bonus/Penalty
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={
                                finance.leagueDayAdjustment > 0
                                  ? `+${finance.leagueDayAdjustment} days`
                                  : `${finance.leagueDayAdjustment} days`
                              }
                              size="small"
                              color={
                                finance.leagueDayAdjustment > 0
                                  ? "warning"
                                  : "success"
                              }
                            />
                          </Box>
                        </Box>
                      )}

                      {(finance.isGuest ||
                        finance.totalLeagueDays > FREE_LEAGUE_DAYS) && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Payable Days
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              flexWrap: "wrap",
                              mt: 0.5,
                            }}
                          >
                            {Array.from(
                              {
                                length: finance.isGuest
                                  ? finance.totalLeagueDays
                                  : finance.totalLeagueDays - FREE_LEAGUE_DAYS,
                              },
                              (_, i) =>
                                finance.isGuest
                                  ? i + 1
                                  : i + FREE_LEAGUE_DAYS + 1,
                            ).map((dayNumber) => {
                              const isPaid =
                                finance.paidLeagueDays.includes(dayNumber);
                              return (
                                <Chip
                                  key={dayNumber}
                                  label={dayNumber}
                                  size="small"
                                  color={isPaid ? "success" : "error"}
                                  onClick={() =>
                                    handleTogglePayment(
                                      finance.playerId,
                                      dayNumber,
                                    )
                                  }
                                  onDelete={() =>
                                    handleTogglePayment(
                                      finance.playerId,
                                      dayNumber,
                                    )
                                  }
                                  deleteIcon={
                                    isPaid ? (
                                      <CheckCircleIcon />
                                    ) : (
                                      <CancelIcon />
                                    )
                                  }
                                  sx={{ cursor: "pointer" }}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      )}

                      <Divider sx={{ mb: 2 }} />

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Total Owed
                          </Typography>
                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            color={
                              finance.totalOwed > 0
                                ? "error.main"
                                : "text.secondary"
                            }
                          >
                            €{finance.totalOwed}
                          </Typography>
                          {finance.isGuest && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              (€{GUEST_PRICE_PER_LEAGUE_DAY}/day)
                            </Typography>
                          )}
                        </Box>
                        {finance.totalOwed > 0 && (
                          <Button
                            variant="contained"
                            size="small"
                            color={
                              copySuccess === finance.playerId
                                ? "success"
                                : "primary"
                            }
                            onClick={() => handleCopyWhatsAppMessage(finance)}
                            startIcon={<ContentCopyIcon />}
                          >
                            Copy Message
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>

            {playerFinances.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No players found in {currentDivision}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 1 && (
        <>
          {/* Analytics Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Filters
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Division</InputLabel>
                    <Select
                      value={analyticsDivision}
                      label="Division"
                      onChange={(e: SelectChangeEvent) =>
                        setAnalyticsDivision(e.target.value as Division | "ALL")
                      }
                    >
                      <MenuItem value="ALL">All Divisions</MenuItem>
                      <MenuItem value="FIRST">First Division</MenuItem>
                      <MenuItem value="SECOND">Second Division</MenuItem>
                      <MenuItem value="THIRD">Third Division</MenuItem>
                      <MenuItem value="WOMEN">Women Division</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Player (Optional)</InputLabel>
                    <Select
                      value={analyticsPlayerId}
                      label="Player (Optional)"
                      onChange={(e: SelectChangeEvent) =>
                        setAnalyticsPlayerId(e.target.value)
                      }
                    >
                      <MenuItem value="">All Players</MenuItem>
                      {players
                        .filter((player) => player.division !== "Test")
                        .map((player) => (
                          <MenuItem key={player.id} value={player.id}>
                            {player.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Player Type</InputLabel>
                    <Select
                      value={
                        guestOnlyFilter === null
                          ? "ALL"
                          : guestOnlyFilter
                            ? "GUEST"
                            : "REGULAR"
                      }
                      label="Player Type"
                      onChange={(e: SelectChangeEvent) => {
                        const value = e.target.value;
                        setGuestOnlyFilter(
                          value === "ALL" ? null : value === "GUEST",
                        );
                      }}
                    >
                      <MenuItem value="ALL">All Players</MenuItem>
                      <MenuItem value="REGULAR">Regular Players Only</MenuItem>
                      <MenuItem value="GUEST">Guest Players Only</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {analyticsLoading ? (
            <Box display="flex" justifyContent="center" p={5}>
              <Typography>Loading analytics...</Typography>
            </Box>
          ) : analyticsData ? (
            <>
              {/* Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography
                        color="text.secondary"
                        gutterBottom
                        variant="body2"
                      >
                        Total Collected
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        €{analyticsData.summary.totalPaid.toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        From {analyticsData.summary.playerCount} players
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography
                        color="text.secondary"
                        gutterBottom
                        variant="body2"
                      >
                        Total Outstanding
                      </Typography>
                      <Typography variant="h4" color="error.main">
                        €{analyticsData.summary.totalOutstanding.toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Still to be collected
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography
                        color="text.secondary"
                        gutterBottom
                        variant="body2"
                      >
                        Total Revenue
                      </Typography>
                      <Typography variant="h4" color="primary.main">
                        €{analyticsData.summary.totalRevenue.toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Collected + Outstanding
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography
                        color="text.secondary"
                        gutterBottom
                        variant="body2"
                      >
                        Avg. per Player
                      </Typography>
                      <Typography variant="h4">
                        €{analyticsData.summary.averagePaidPerPlayer.toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Average collected
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Player Type Breakdown */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Player Type Breakdown
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Type</strong>
                          </TableCell>
                          <TableCell align="center">
                            <strong>Count</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>Total Paid</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>Total Outstanding</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>Total</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>
                            <Chip
                              label="Regular Players"
                              color="primary"
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            {analyticsData.summary.regularPlayerCount}
                          </TableCell>
                          <TableCell align="right">
                            €{analyticsData.summary.regularTotalPaid.toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            €
                            {analyticsData.summary.regularTotalOutstanding.toFixed(
                              2,
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <strong>
                              €
                              {(
                                analyticsData.summary.regularTotalPaid +
                                analyticsData.summary.regularTotalOutstanding
                              ).toFixed(2)}
                            </strong>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Chip
                              label="Guest Players"
                              sx={{
                                backgroundColor: "#ff9800",
                                color: "white",
                              }}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            {analyticsData.summary.guestPlayerCount}
                          </TableCell>
                          <TableCell align="right">
                            €{analyticsData.summary.guestTotalPaid.toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            €
                            {analyticsData.summary.guestTotalOutstanding.toFixed(
                              2,
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <strong>
                              €
                              {(
                                analyticsData.summary.guestTotalPaid +
                                analyticsData.summary.guestTotalOutstanding
                              ).toFixed(2)}
                            </strong>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Division Breakdown */}
              {analyticsDivision === "ALL" &&
                analyticsData.divisionBreakdown.length > 0 && (
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Division Breakdown
                      </Typography>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <strong>Division</strong>
                              </TableCell>
                              <TableCell align="center">
                                <strong>Players</strong>
                              </TableCell>
                              <TableCell align="center">
                                <strong>Regular</strong>
                              </TableCell>
                              <TableCell align="center">
                                <strong>Guest</strong>
                              </TableCell>
                              <TableCell align="right">
                                <strong>Total Paid</strong>
                              </TableCell>
                              <TableCell align="right">
                                <strong>Outstanding</strong>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {analyticsData.divisionBreakdown.map((div) => (
                              <TableRow key={div.division}>
                                <TableCell>
                                  <Chip
                                    label={div.division}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  {div.playerCount}
                                </TableCell>
                                <TableCell align="center">
                                  {div.regularPlayerCount}
                                </TableCell>
                                <TableCell align="center">
                                  {div.guestPlayerCount}
                                </TableCell>
                                <TableCell align="right">
                                  €{div.totalPaid.toFixed(2)}
                                </TableCell>
                                <TableCell align="right">
                                  €{div.totalOutstanding.toFixed(2)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                )}

              {/* Top Contributors */}
              {analyticsData.topContributors.length > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Top 10 Contributors (By Amount Paid)
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <strong>Player</strong>
                            </TableCell>
                            <TableCell align="center">
                              <strong>Division</strong>
                            </TableCell>
                            <TableCell align="center">
                              <strong>League Days</strong>
                            </TableCell>
                            <TableCell align="right">
                              <strong>Amount Paid</strong>
                            </TableCell>
                            <TableCell align="right">
                              <strong>Outstanding</strong>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {analyticsData.topContributors.map(
                            (player, index) => (
                              <TableRow key={player.playerId}>
                                <TableCell>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      #{index + 1}
                                    </Typography>
                                    {player.playerName}
                                    {player.isGuest && (
                                      <Chip
                                        label="GUEST"
                                        size="small"
                                        sx={{
                                          backgroundColor: "#ff9800",
                                          color: "white",
                                          fontSize: "0.65rem",
                                        }}
                                      />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={player.division}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  {player.totalLeagueDays}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    color="success.main"
                                    fontWeight="bold"
                                  >
                                    €{player.amountPaid.toFixed(2)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  €{player.amountOutstanding.toFixed(2)}
                                </TableCell>
                              </TableRow>
                            ),
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Alert severity="info">
              No analytics data available. Adjust filters and try again.
            </Alert>
          )}
        </>
      )}

      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Payment Reminder Log
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Every payment reminder email that was sent (automatically or manually), when it was sent, and to whom.
            </Typography>
            {reminderLogLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : reminderLog.length === 0 ? (
              <Alert severity="info">No payment reminder emails have been sent yet.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Sent At</TableCell>
                      <TableCell>Player</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell align="right">Amount Owed</TableCell>
                      <TableCell>Triggered By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reminderLog.map((entry) => (
                      <TableRow
                        key={entry.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => setSelectedLogEntry(entry)}
                      >
                        <TableCell>
                          {new Date(entry.sentAt).toLocaleString()}
                        </TableCell>
                        <TableCell>{entry.playerName}</TableCell>
                        <TableCell>{entry.email}</TableCell>
                        <TableCell align="center">
                          {entry.success ? (
                            <CheckCircleIcon fontSize="small" color="success" />
                          ) : (
                            <ErrorOutlineIcon fontSize="small" color="error" />
                          )}
                        </TableCell>
                        <TableCell>{entry.subject || "-"}</TableCell>
                        <TableCell align="right">
                          €{entry.totalOwed.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {entry.triggeredBy === "manual"
                            ? "Manual"
                            : entry.triggeredBy.startsWith("leagueDay:")
                              ? "Automatic"
                              : entry.triggeredBy}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payment Reminder Log Detail Dialog */}
      <Dialog
        open={selectedLogEntry !== null}
        onClose={() => setSelectedLogEntry(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Payment Reminder Details</DialogTitle>
        <DialogContent dividers>
          {selectedLogEntry && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Sent to {selectedLogEntry.playerName} ({selectedLogEntry.email}) at{" "}
                {new Date(selectedLogEntry.sentAt).toLocaleString()}
              </Typography>
              {!selectedLogEntry.success && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {selectedLogEntry.errorMessage || "This email failed to send."}
                </Alert>
              )}
              {selectedLogEntry.unpaidLeagueDays.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Unpaid League Days</Typography>
                  <ul>
                    {selectedLogEntry.unpaidLeagueDays.map((d) => (
                      <li key={d.dayNumber}>
                        {d.date} — Division {d.division}
                      </li>
                    ))}
                  </ul>
                </Box>
              )}
              <Typography variant="subtitle2" sx={{ mt: 2 }}>
                Subject
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {selectedLogEntry.subject || "(no subject — email was not sent)"}
              </Typography>
              <Typography variant="subtitle2">Email Content</Typography>
              {selectedLogEntry.htmlBody ? (
                <Box
                  sx={{
                    mt: 1,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    maxHeight: 400,
                    overflow: "auto",
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedLogEntry.htmlBody }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No email content available.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedLogEntry(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Payment Reminder Compose/Preview Dialog */}
      <Dialog
        open={reminderDialogFinance !== null}
        onClose={handleCloseReminderDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Send Payment Reminder{reminderDialogFinance ? ` — ${reminderDialogFinance.playerName}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {reminderDialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {reminderDialogError}
            </Alert>
          )}
          <TextField
            label="Subject"
            fullWidth
            value={reminderSubjectDraft}
            onChange={(e) => setReminderSubjectDraft(e.target.value)}
            disabled={reminderSending}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Email HTML"
            fullWidth
            multiline
            minRows={8}
            maxRows={16}
            value={reminderHtmlBodyDraft}
            onChange={(e) => setReminderHtmlBodyDraft(e.target.value)}
            disabled={reminderSending}
            sx={{ mb: 2, fontFamily: "monospace" }}
          />
          <Typography variant="subtitle2" gutterBottom>
            Preview
          </Typography>
          <Box
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              maxHeight: 300,
              overflow: "auto",
            }}
            dangerouslySetInnerHTML={{ __html: reminderHtmlBodyDraft }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReminderDialog} disabled={reminderSending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmSendReminderEmail}
            disabled={reminderSending || !reminderSubjectDraft || !reminderHtmlBodyDraft}
            startIcon={reminderSending ? <CircularProgress size={16} /> : <MarkEmailReadIcon />}
          >
            Send Email
          </Button>
        </DialogActions>
      </Dialog>

      {/* Player Details Dialog */}
      <Dialog
        open={selectedPlayer !== null}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            {selectedPlayer?.playerName}
            {selectedPlayer?.isGuest && (
              <Chip
                label="GUEST"
                size="small"
                sx={{
                  backgroundColor: "#ff9800",
                  color: "white",
                  fontWeight: "bold",
                }}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Registered in: {selectedPlayer?.registeredDivision}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography variant="h6" gutterBottom>
              Summary
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Chip
                label={`Total Days: ${
                  playerLeagueDays.length +
                  (selectedPlayer?.trialDaysCount || 0)
                }`}
                color="primary"
              />
              <Chip
                label={`Own Division: ${
                  playerLeagueDays.filter(
                    (ld) => ld.division === selectedPlayer?.registeredDivision,
                  ).length
                }`}
                color="info"
              />
              <Chip
                label={`Other Divisions: ${
                  playerLeagueDays.filter(
                    (ld) => ld.division !== selectedPlayer?.registeredDivision,
                  ).length
                }`}
                color="info"
              />
              {(selectedPlayer?.trialDaysCount || 0) > 0 && (
                <Chip
                  label={`Trial Days: ${selectedPlayer?.trialDaysCount || 0}`}
                  color="warning"
                />
              )}
              {(selectedPlayer?.leagueDayAdjustment || 0) !== 0 && (
                <Chip
                  label={`Bonus/Penalty: ${
                    (selectedPlayer?.leagueDayAdjustment || 0) > 0
                      ? `+${selectedPlayer?.leagueDayAdjustment}`
                      : selectedPlayer?.leagueDayAdjustment
                  } days`}
                  color={
                    (selectedPlayer?.leagueDayAdjustment || 0) > 0
                      ? "warning"
                      : "success"
                  }
                />
              )}
              <Chip
                label={`Total Owed: €${selectedPlayer?.totalOwed || 0}`}
                color={
                  (selectedPlayer?.totalOwed || 0) > 0 ? "error" : "success"
                }
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Bonuses and Penalties Section */}
          {selectedPlayer && (
            <>
              {(() => {
                const playerPenaltiesAndBonuses = penalties.filter(
                  (p: any) =>
                    p.playerId === selectedPlayer.playerId &&
                    !p.isDisqualification,
                );
                const hasPenaltiesOrBonuses =
                  playerPenaltiesAndBonuses.length > 0;

                if (!hasPenaltiesOrBonuses) return null;

                return (
                  <>
                    <Typography variant="h6" gutterBottom>
                      Bonuses & Penalties
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      {playerPenaltiesAndBonuses.map((record: any) => (
                        <Card
                          key={record.id}
                          sx={{
                            mb: 1,
                            backgroundColor:
                              record.type === "penalty"
                                ? "rgba(211, 47, 47, 0.05)"
                                : "rgba(76, 175, 80, 0.05)",
                            border: 1,
                            borderColor:
                              record.type === "penalty"
                                ? "error.light"
                                : "success.light",
                          }}
                        >
                          <CardContent
                            sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                mb: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <Chip
                                label={
                                  record.type === "penalty"
                                    ? "Penalty"
                                    : "Bonus"
                                }
                                size="small"
                                color={
                                  record.type === "penalty"
                                    ? "error"
                                    : "success"
                                }
                              />
                              {record.points > 0 && (
                                <Chip
                                  label={`${
                                    record.type === "penalty" ? "-" : "+"
                                  }${record.points} pts`}
                                  size="small"
                                  variant="outlined"
                                  color={
                                    record.type === "penalty"
                                      ? "error"
                                      : "success"
                                  }
                                />
                              )}
                              {record.leagueDayAdjustment > 0 && (
                                <Chip
                                  label={`${
                                    record.type === "penalty" ? "+" : "-"
                                  }${record.leagueDayAdjustment} days`}
                                  size="small"
                                  variant="outlined"
                                  color={
                                    record.type === "penalty"
                                      ? "warning"
                                      : "info"
                                  }
                                />
                              )}
                              <Chip
                                label={new Date(
                                  record.createdAt,
                                ).toLocaleDateString()}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                            {record.reason && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                <strong>Reason:</strong> {record.reason}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                    <Divider sx={{ my: 2 }} />
                  </>
                );
              })()}
            </>
          )}

          <Typography variant="h6" gutterBottom>
            League Day Participation
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {selectedPlayer?.isGuest
              ? `Guest players pay €${GUEST_PRICE_PER_LEAGUE_DAY} per day from day 1`
              : `Regular players: First ${FREE_LEAGUE_DAYS} days free, then €${PRICE_PER_LEAGUE_DAY} per day`}
          </Typography>

          {playerLeagueDays.length > 0 ? (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Day #</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Date</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Division</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Payment Status</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {playerLeagueDays.map((ld) => (
                    <TableRow
                      key={ld.leagueDayId}
                      sx={{
                        backgroundColor:
                          ld.isPayable && !ld.isPaid
                            ? "rgba(211, 47, 47, 0.1)"
                            : ld.isPayable && ld.isPaid
                              ? "rgba(46, 125, 50, 0.1)"
                              : "inherit",
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={ld.dayNumber}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(ld.date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ld.division}
                          size="small"
                          color={
                            ld.division === selectedPlayer?.registeredDivision
                              ? "info"
                              : "default"
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        {!ld.isPayable ? (
                          <Chip
                            label="Free"
                            size="small"
                            color="success"
                            icon={<CheckCircleIcon />}
                          />
                        ) : ld.isPaid ? (
                          <Chip
                            label="Paid"
                            size="small"
                            color="success"
                            icon={<CheckCircleIcon />}
                            onClick={() =>
                              handleTogglePayment(
                                selectedPlayer!.playerId,
                                ld.dayNumber,
                              )
                            }
                            sx={{ cursor: "pointer" }}
                          />
                        ) : (
                          <Chip
                            label={`Unpaid - €${
                              selectedPlayer?.isGuest
                                ? GUEST_PRICE_PER_LEAGUE_DAY
                                : PRICE_PER_LEAGUE_DAY
                            }`}
                            size="small"
                            color="error"
                            icon={<CancelIcon />}
                            onClick={() =>
                              handleTogglePayment(
                                selectedPlayer!.playerId,
                                ld.dayNumber,
                              )
                            }
                            sx={{ cursor: "pointer" }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              No league day participation found
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          {selectedPlayer && selectedPlayer.totalOwed > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<ContentCopyIcon />}
              onClick={() => {
                handleCopyWhatsAppMessage(selectedPlayer);
                handleCloseDialog();
              }}
            >
              Copy Payment Message
            </Button>
          )}
          {selectedPlayer && selectedPlayer.totalOwed > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={
                  sendingEmailId === selectedPlayer.playerId ? (
                    <CircularProgress size={16} />
                  ) : (
                    <MarkEmailReadIcon />
                  )
                }
                disabled={sendingEmailId === selectedPlayer.playerId}
                onClick={() => handleOpenReminderDialog(selectedPlayer)}
              >
                Send Payment Reminder Email
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {formatLastReminderText(selectedPlayer.playerId)}
              </Typography>
            </Box>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Finances;
